// CR038: Community Candidate Detection Summary Panel

import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import type { CommunityCandidateScoringConfig } from '@/lib/community-signals/types';

interface LastRunStats {
  items_scanned: number;
  candidates_flagged: number;
  candidate_rate: string;
  avg_score: string;
  top_signals: { signal_id: string; fire_count: number }[];
}

interface CommunityCandidateSummaryProps {
  config: CommunityCandidateScoringConfig | null;
  isLoading: boolean;
  lastRunStats?: LastRunStats | null;
}

export function CommunityCandidateSummary({ 
  config, 
  isLoading,
  lastRunStats 
}: CommunityCandidateSummaryProps) {
  if (isLoading) {
    return (
      <div className="space-y-3 p-4">
        <Skeleton className="h-4 w-32" />
        <div className="grid grid-cols-4 gap-4">
          <Skeleton className="h-16" />
          <Skeleton className="h-16" />
          <Skeleton className="h-16" />
          <Skeleton className="h-16" />
        </div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        No active configuration found for this scope.
      </div>
    );
  }

  const enabledSignals = config.signals.filter(s => s.enabled);

  return (
    <div className="space-y-4 p-4">
      {/* Config Info */}
      <div className="flex items-center gap-3">
        <Badge variant={config.enabled ? "default" : "secondary"}>
          {config.enabled ? "Enabled" : "Disabled"}
        </Badge>
        <span className="text-sm text-muted-foreground">
          Version: {config.version}
        </span>
        <span className="text-sm text-muted-foreground">
          Scope: {config.scope}
        </span>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-muted/50 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold">{config.threshold}</div>
          <div className="text-xs text-muted-foreground">Threshold</div>
        </div>
        <div className="bg-muted/50 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold">{enabledSignals.length}</div>
          <div className="text-xs text-muted-foreground">Active Signals</div>
        </div>
        <div className="bg-muted/50 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold">
            {lastRunStats?.items_scanned ?? '--'}
          </div>
          <div className="text-xs text-muted-foreground">Items Scanned</div>
        </div>
        <div className="bg-muted/50 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold">{lastRunStats?.candidate_rate ?? '--'}%</div>
          <div className="text-xs text-muted-foreground">Candidate Rate</div>
        </div>
      </div>

      {/* Top Signals */}
      {lastRunStats?.top_signals && lastRunStats.top_signals.length > 0 && (
        <div className="space-y-1">
          <div className="text-xs text-muted-foreground">Top Signals Fired:</div>
          <div className="flex flex-wrap gap-1">
            {lastRunStats.top_signals.slice(0, 5).map((sig, i) => (
              <Badge key={i} variant="outline" className="text-xs">
                {sig.signal_id} ({sig.fire_count})
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Quick stats about signal weights */}
      <div className="text-xs text-muted-foreground border-t pt-3">
        <span className="font-medium">Signal weights: </span>
        {enabledSignals.length > 0 ? (
          <>
            Max positive: +{Math.max(...enabledSignals.map(s => s.weight).filter(w => w > 0), 0)} | 
            Max negative: {Math.min(...enabledSignals.map(s => s.weight).filter(w => w < 0), 0) || 0}
          </>
        ) : (
          'No signals configured'
        )}
      </div>
    </div>
  );
}
